import { create } from 'zustand'
import { CanonicalCut, IntegerProblem } from '../../core/cuts'
import type { BnBNode } from '../../game/bnb-manager'
import {
  BnBFlowEvent,
  BnBFlowPhase,
  CutFlowEvent,
  CutFlowPhase,
  initialCutFlowState,
  reduceBnBFlow,
  reduceCutFlow,
} from './flow-reducers'

export type AppMode = 'loading' | 'auth' | 'playing' | 'editing' | 'gameOver'
export type PanelState = 'expanded' | 'minimized'
export type ModalId = 'gameOver' | 'optimalSolution'

export interface CutHistoryItem {
  cutNumber: number
  cut: CanonicalCut
  cutType: string
  volumeReduction: number
  objectiveValueReduction: number
  originalVolume: number
  newVolume: number
  originalObjectiveValue: number
  newObjectiveValue: number
  optimalSolution: number[]
  timestamp: Date
}

export interface GameStats {
  cutsRemaining: number
  cutsUsed: number
  volumeReduction: number
  currentScore: number
  initialVolume: number
  currentVolume: number
  initialObjectiveValue: number
  currentObjectiveValue: number
  initialIPOptimalValue: number
}

export interface CutWorkflow {
  phase: CutFlowPhase
  availableCuts: CanonicalCut[]
  cutsHistory: CutHistoryItem[]
  selectedCutIndex: number | null
  selectedCut: CanonicalCut | null
  muValue: number | null
  lastError: string | null
}

export interface PanelStates {
  math: PanelState
  cut: PanelState
  history: PanelState
  bnb: PanelState
  mobile: boolean
}

export interface BnBState {
  nodes: Map<string, BnBNode>
  rootNodeId: string | null
  currentNodeId: string | null
  incumbentNodeId: string | null
  maxNodes: number
  phase: BnBFlowPhase
  commandLocked: boolean
  lastError: string | null
}

export interface UIState {
  mode: AppMode
  panels: PanelStates
  cutWorkflow: CutWorkflow
  bnbState: BnBState
  activeModal: ModalId | null
  optimalSolutionModal: {
    optimalValue: number
    cutsApplied: number
    volumeReduction: number
    initialObjectiveValue: number
    initialIPOptimalValue: number
  } | null
  loading: boolean
  notifications: Array<{
    id: string
    message: string
    type: 'success' | 'error' | 'info' | 'warning'
    timestamp: number
  }>
  mobileMenuOpen: boolean
  activeTab: string
}

export interface GameState {
  currentProblem: IntegerProblem | null
  originalProblem: IntegerProblem | null
  stats: GameStats
  cutsLimit: number
}

export interface SettingsState {
  cutsLimit: number
  soundEnabled: boolean
  animationsEnabled: boolean
}

export interface GameStore {
  ui: UIState
  game: GameState
  settings: SettingsState

  cutsLimitCallback?: (limit: number) => void

  setMode: (mode: AppMode) => void
  setLoading: (loading: boolean) => void
  setPanelState: (panel: keyof PanelStates, state: PanelState) => void
  togglePanel: (panel: keyof PanelStates) => void
  addNotification: (message: string, type?: 'success' | 'error' | 'info' | 'warning') => void
  removeNotification: (id: string) => void

  setCurrentProblem: (problem: IntegerProblem | null) => void
  setOriginalProblem: (problem: IntegerProblem | null) => void
  updateStats: (stats: Partial<GameStats>) => void
  setCutsLimit: (limit: number) => void

  setAvailableCuts: (cuts: CanonicalCut[]) => void
  dispatchCutFlowEvent: (event: CutFlowEvent) => void
  addCutToHistory: (item: CutHistoryItem) => void
  setCutsHistory: (history: CutHistoryItem[]) => void
  clearCutsHistory: () => void
  setCutsLimitCallback: (callback: ((limit: number) => void) | undefined) => void

  updateBnBState: (updates: Partial<BnBState>) => void
  dispatchBnBFlowEvent: (event: BnBFlowEvent) => void
  resetBnB: () => void

  showModal: (modalId: ModalId) => void
  showOptimalSolutionModal: (payload: {
    optimalValue: number
    cutsApplied: number
    volumeReduction: number
    initialObjectiveValue: number
    initialIPOptimalValue: number
  }) => void
  hideModal: () => void

  resetGame: () => void
}

const createInitialCutWorkflow = (): CutWorkflow => {
  const initial = initialCutFlowState()
  return {
    phase: initial.phase,
    availableCuts: initial.availableCuts,
    cutsHistory: [],
    selectedCutIndex: initial.selectedCutIndex,
    selectedCut: initial.selectedCut,
    muValue: initial.muValue,
    lastError: initial.lastError,
  }
}

const createInitialBnBState = (): BnBState => ({
  nodes: new Map(),
  rootNodeId: null,
  currentNodeId: null,
  incumbentNodeId: null,
  maxNodes: 7,
  phase: 'idle',
  commandLocked: false,
  lastError: null,
})

const isBnBLockingEvent = (event: BnBFlowEvent): boolean => {
  return event.type === 'BNB_BRANCH_REQUESTED' || event.type === 'BNB_BACKTRACK_REQUESTED'
}

export const useGameStore = create<GameStore>((set, get) => ({
  ui: {
    mode: 'loading',
    panels: {
      math: 'expanded',
      cut: 'expanded',
      history: 'expanded',
      bnb: 'expanded',
      mobile: false,
    },
    cutWorkflow: createInitialCutWorkflow(),
    bnbState: createInitialBnBState(),
    activeModal: null,
    optimalSolutionModal: null,
    loading: false,
    notifications: [],
    mobileMenuOpen: false,
    activeTab: '',
  },
  game: {
    currentProblem: null,
    originalProblem: null,
    stats: {
      cutsRemaining: 15,
      cutsUsed: 0,
      volumeReduction: 0,
      currentScore: 0,
      initialVolume: 0,
      currentVolume: 0,
      initialObjectiveValue: 0,
      currentObjectiveValue: 0,
      initialIPOptimalValue: 0,
    },
    cutsLimit: 15,
  },
  settings: {
    cutsLimit: 15,
    soundEnabled: true,
    animationsEnabled: true,
  },

  setMode: (mode) =>
    set((state) => ({
      ui: { ...state.ui, mode },
    })),

  setLoading: (loading) =>
    set((state) => ({
      ui: { ...state.ui, loading },
    })),

  setPanelState: (panel, panelState) =>
    set((state) => ({
      ui: {
        ...state.ui,
        panels: { ...state.ui.panels, [panel]: panelState },
      },
    })),

  togglePanel: (panel) =>
    set((state) => {
      const currentState = state.ui.panels[panel]
      if (panel === 'mobile') {
        return { ui: { ...state.ui, panels: { ...state.ui.panels, mobile: !state.ui.panels.mobile } } }
      }
      const newState = currentState === 'expanded' ? 'minimized' : 'expanded'
      return {
        ui: {
          ...state.ui,
          panels: { ...state.ui.panels, [panel]: newState },
        },
      }
    }),

  addNotification: (message, type = 'info') =>
    set((state) => {
      const id = `notification-${Date.now()}-${Math.random()}`
      const notification = { id, message, type, timestamp: Date.now() }

      setTimeout(() => {
        get().removeNotification(id)
      }, 5000)

      return {
        ui: {
          ...state.ui,
          notifications: [...state.ui.notifications, notification],
        },
      }
    }),

  removeNotification: (id) =>
    set((state) => ({
      ui: {
        ...state.ui,
        notifications: state.ui.notifications.filter((n) => n.id !== id),
      },
    })),

  setCurrentProblem: (problem) =>
    set((state) => ({
      game: { ...state.game, currentProblem: problem },
    })),

  setOriginalProblem: (problem) =>
    set((state) => ({
      game: { ...state.game, originalProblem: problem },
    })),

  updateStats: (stats) =>
    set((state) => ({
      game: {
        ...state.game,
        stats: { ...state.game.stats, ...stats },
      },
    })),

  setCutsLimit: (limit) =>
    set((state) => ({
      game: { ...state.game, cutsLimit: limit, stats: { ...state.game.stats, cutsRemaining: limit } },
      settings: { ...state.settings, cutsLimit: limit },
    })),

  setAvailableCuts: (cuts) =>
    set((state) => {
      const next = reduceCutFlow(
        {
          phase: state.ui.cutWorkflow.phase,
          availableCuts: state.ui.cutWorkflow.availableCuts,
          cutsHistoryCount: state.ui.cutWorkflow.cutsHistory.length,
          selectedCutIndex: state.ui.cutWorkflow.selectedCutIndex,
          selectedCut: state.ui.cutWorkflow.selectedCut,
          muValue: state.ui.cutWorkflow.muValue,
          lastError: state.ui.cutWorkflow.lastError,
        },
        { type: 'CUT_LIST_REFRESHED', cuts },
      )

      return {
        ui: {
          ...state.ui,
          cutWorkflow: {
            ...state.ui.cutWorkflow,
            phase: next.phase,
            availableCuts: next.availableCuts,
            selectedCutIndex: next.selectedCutIndex,
            selectedCut: next.selectedCut,
            muValue: next.muValue,
            lastError: next.lastError,
          },
        },
      }
    }),

  dispatchCutFlowEvent: (event) =>
    set((state) => {
      const next = reduceCutFlow(
        {
          phase: state.ui.cutWorkflow.phase,
          availableCuts: state.ui.cutWorkflow.availableCuts,
          cutsHistoryCount: state.ui.cutWorkflow.cutsHistory.length,
          selectedCutIndex: state.ui.cutWorkflow.selectedCutIndex,
          selectedCut: state.ui.cutWorkflow.selectedCut,
          muValue: state.ui.cutWorkflow.muValue,
          lastError: state.ui.cutWorkflow.lastError,
        },
        event,
      )

      return {
        ui: {
          ...state.ui,
          cutWorkflow: {
            ...state.ui.cutWorkflow,
            phase: next.phase,
            availableCuts: next.availableCuts,
            selectedCutIndex: next.selectedCutIndex,
            selectedCut: next.selectedCut,
            muValue: next.muValue,
            lastError: next.lastError,
          },
        },
      }
    }),

  addCutToHistory: (item) =>
    set((state) => ({
      ui: {
        ...state.ui,
        cutWorkflow: {
          ...state.ui.cutWorkflow,
          cutsHistory: [...state.ui.cutWorkflow.cutsHistory, item],
        },
      },
    })),

  setCutsHistory: (history) =>
    set((state) => ({
      ui: {
        ...state.ui,
        cutWorkflow: {
          ...state.ui.cutWorkflow,
          cutsHistory: history,
        },
      },
    })),

  clearCutsHistory: () =>
    set((state) => ({
      ui: {
        ...state.ui,
        cutWorkflow: { ...state.ui.cutWorkflow, cutsHistory: [] },
      },
    })),

  setCutsLimitCallback: (cutsLimitCallback) => set(() => ({ cutsLimitCallback })),

  updateBnBState: (updates) =>
    set((state) => ({
      ui: {
        ...state.ui,
        bnbState: { ...state.ui.bnbState, ...updates },
      },
    })),

  dispatchBnBFlowEvent: (event) =>
    set((state) => {
      const phase = reduceBnBFlow(state.ui.bnbState.phase, event)
      const commandLocked = isBnBLockingEvent(event)
        ? true
        : event.type === 'BNB_STARTED' ||
            event.type === 'BNB_FINISHED' ||
            event.type === 'BNB_NODE_SELECTED' ||
            event.type === 'BNB_LIMIT_CHANGED' ||
            event.type === 'BNB_RESET_REQUESTED' ||
            event.type === 'BNB_COMMAND_COMPLETED'
          ? false
          : state.ui.bnbState.commandLocked

      return {
        ui: {
          ...state.ui,
          bnbState: {
            ...state.ui.bnbState,
            phase,
            commandLocked,
          },
        },
      }
    }),

  resetBnB: () =>
    set((state) => ({
      ui: {
        ...state.ui,
        bnbState: createInitialBnBState(),
      },
    })),

  showModal: (modalId) =>
    set((state) => ({
      ui: {
        ...state.ui,
        activeModal: modalId,
        optimalSolutionModal: modalId === 'optimalSolution' ? state.ui.optimalSolutionModal : null,
      },
    })),

  showOptimalSolutionModal: (payload) =>
    set((state) => ({
      ui: {
        ...state.ui,
        activeModal: 'optimalSolution',
        optimalSolutionModal: payload,
      },
    })),

  hideModal: () =>
    set((state) => ({
      ui: { ...state.ui, activeModal: null, optimalSolutionModal: null },
    })),

  resetGame: () =>
    set((state) => ({
      game: {
        currentProblem: null,
        originalProblem: null,
        stats: {
          cutsRemaining: state.settings.cutsLimit,
          cutsUsed: 0,
          volumeReduction: 0,
          currentScore: 0,
          initialVolume: 0,
          currentVolume: 0,
          initialObjectiveValue: 0,
          currentObjectiveValue: 0,
          initialIPOptimalValue: 0,
        },
        cutsLimit: state.settings.cutsLimit,
      },
      ui: {
        ...state.ui,
        cutWorkflow: createInitialCutWorkflow(),
      },
    })),
}))
